import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Database, Play, CheckCircle2, AlertTriangle } from "lucide-react";

export default function MigrationTab() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  const loadStatus = async () => {
    const { data } = await api.get("/migration/status");
    setStatus(data);
  };
  useEffect(() => { loadStatus(); }, []);

  const dryRun = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/migration/backfill-enrollments?dry_run=true");
      setPreview(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const commit = async () => {
    if (!window.confirm(`This will create ${preview.students_to_migrate} enrollments and relink ${preview.payments_to_relink} payments. Continue?`)) return;
    setRunning(true);
    try {
      const { data } = await api.post("/migration/backfill-enrollments?dry_run=false");
      toast.success(`Created ${data.enrollments_created} enrollments · relinked ${data.payments_relinked} payments`);
      setPreview(null);
      await loadStatus();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  return (
    <div data-testid="migration-tab">
      <div className="mb-4">
        <h3 className="font-display text-lg font-semibold text-secondary">Data migration</h3>
        <p className="text-sm text-muted-foreground">Convert legacy students into the Enrollment model. Always dry-run first.</p>
      </div>

      {status && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[["Total students", status.total_students], ["With enrollment", status.students_with_enrollment],
            ["Pending", status.students_pending], ["Unlinked payments", status.unlinked_payments]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">{l}</p>
              <p className="font-display text-xl font-bold text-secondary">{v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 text-secondary"><Database className="h-5 w-5 text-primary" /><span className="font-medium">Backfill enrollments</span></div>
        <p className="mt-1 text-sm text-muted-foreground">Creates one enrollment per legacy student and relinks their payments. Safe to re-run (idempotent).</p>

        <div className="mt-4 flex gap-3">
          <Button variant="outline" onClick={dryRun} disabled={running} data-testid="migration-dryrun-button"><Play className="mr-1.5 h-4 w-4" /> Dry run</Button>
          <Button onClick={commit} disabled={running || !preview || preview.students_to_migrate === 0} data-testid="migration-commit-button"><CheckCircle2 className="mr-1.5 h-4 w-4" /> Run migration</Button>
        </div>

        {preview && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="migration-preview">
            <div className="flex items-center gap-2 text-amber-800"><AlertTriangle className="h-4 w-4" /><span className="text-sm font-medium">Dry run preview</span></div>
            <p className="mt-2 text-sm text-secondary">
              <Badge className="border-0 bg-primary/10 text-primary">{preview.students_to_migrate}</Badge> students will be migrated ·{" "}
              <Badge className="border-0 bg-primary/10 text-primary">{preview.payments_to_relink}</Badge> payments relinked.
            </p>
            {preview.sample?.length > 0 && <p className="mt-2 text-xs text-muted-foreground">Sample: {preview.sample.map((s) => s.student_code).join(", ")}</p>}
            {preview.students_to_migrate === 0 && <p className="mt-2 text-sm text-accent">Nothing to migrate — all students already have enrollments. ✅</p>}
          </div>
        )}
      </div>
    </div>
  );
}
